import { useEffect } from 'react';
import { ModalComponentWrapper } from '@/shared/components';
import {
  isMfaError,
  MfaConfirmationScreen,
  MfaIdentitiesList,
  sendTwoFactorIdentityCode,
  useMfaConfirmationModalForm,
  useMfaFlow,
  type MfaRequiredResponseData,
  type TwoFA,
} from '@/entities/mfa-identity';
import { useRequestStatus } from '@/shared/lib';
import { isVerificationCodeInvalidError } from '@/shared/utils';

type Props = {
  mfaData: MfaRequiredResponseData;
  requestFn: (twoFA: TwoFA) => Promise<unknown>;
  onSuccess: (response: unknown) => void;
  onBack?: () => void;
};

const MfaConfirmationModal = ({
  mfaData,
  requestFn,
  onSuccess,
  onBack,
}: Props) => {
  const { isLoading, setIsLoading, error, setError } = useRequestStatus();

  const { form, resetCode } = useMfaConfirmationModalForm();
  const mfaFlow = useMfaFlow(form);

  useEffect(() => {
    mfaFlow.startMfaFlow(mfaData);
  }, [mfaData]);

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await requestFn(form.getValues('twoFA'));

      if (!isMfaError(response)) {
        onSuccess(response);
        return;
      }

      if (isVerificationCodeInvalidError(response)) {
        resetCode();
        return;
      }

      setError(response.message);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ModalComponentWrapper
      title="authPages.identityConfirmationForm.title"
      description={mfaFlow.modalDescriptionText}
      onBack={onBack}>
      {mfaFlow.view === 'screen' && mfaFlow.selectedIdentity && (
        <MfaConfirmationScreen
          form={form}
          identityType={mfaFlow.selectedIdentity.name}
          mfaCodeFieldName="twoFA.code"
          sendOtpCodeMutationFn={sendTwoFactorIdentityCode}
          setMaskedContact={mfaFlow.setMaskedContact}
          onSubmit={handleSubmit}
          isSubmitLoading={isLoading}
          submitError={error || undefined}
        />
      )}

      {mfaFlow.view === 'list' && mfaFlow.identityList && (
        <MfaIdentitiesList
          availableIdentities={mfaFlow.identityList}
          onSelectIdentity={mfaFlow.selectIdentity}
        />
      )}
    </ModalComponentWrapper>
  );
};

export { MfaConfirmationModal };
